import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Mountain, Waves, Sunrise, Compass, Award, Wind } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import ParallaxImage from "@/components/ParallaxImage";
import JourneyFinder from "@/components/JourneyFinder";

const relates = [
  "I want to support and get the best out of my staff",
  "I want to get clear on my goals and aspirations",
  "I feel overwhelmed in life and want to find balance",
  "I would love a better culture in the business",
];

const timeline = [
  {
    num: "01",
    era: "Two decades ago",
    title: "The journey begins",
    text: "Dave starts a personal development journey that will run for over 20 years, testing every system, pattern and process to find what actually works.",
    img: "/images/rx4/dave-rocks.webp",
    alt: "Dave sat by the lake with his RX4 pack",
  },
  {
    num: "02",
    era: "The last three years",
    title: "The turning point",
    text: "Dave truly finds the person he was looking for. That person becomes the business, and RX4 Lakes is born as an extension of it.",
    img: "/images/rx4/dave-ennerdale-bag.webp",
    alt: "Dave standing by Ennerdale lake",
  },
  {
    num: "03",
    era: "Professional accreditation",
    title: "Master Coach",
    text: "An accredited Master Coach, guiding people into a better understanding of themselves with structure, honesty and radical responsibility.",
    img: "/images/rx4/workshop-keynote.webp",
    alt: "Dave delivering a workshop",
  },
  {
    num: "04",
    era: "Into the cold",
    title: "Wim Hof Method Instructor",
    text: "Breathwork and cold exposure become core tools, taught properly and safely by a certified instructor.",
    img: "/images/rx4/dave-sea-dip.jpg",
    alt: "Dave mid-dip at St Bees",
  },
  {
    num: "05",
    era: "Today",
    title: "Guiding others",
    text: "Helping businesses and individuals flourish to become efficient and effective, across programmes, coaching, talks and community experiences.",
    img: "/images/rx4/dave-summit.webp",
    alt: "Dave with arms raised on a fell at sunset",
  },
];

const values = [
  { num: "01", text: "Dream big" },
  { num: "02", text: "Start small" },
  { num: "03", text: "Be ridiculously faithful" },
  { num: "04", text: "Focus on what you can control" },
  { num: "05", text: "Be inspired by your own experiences" },
];

const pillars = [
  { num: "01", title: "RESET", text: "Clear mental clutter. Regulate your nervous system with breathwork and cold water immersion, and arrive back at yourself.", icon: Waves },
  { num: "02", title: "REFOCUS", text: "Sharpen strategic direction. Get clear on your goals, focus on what you can control, and let the rest go.", icon: Compass },
  { num: "03", title: "RECHARGE", text: "Build physical resilience. Reap the rewards of connecting with nature and unplugging from the constant noise.", icon: Mountain },
  { num: "04", title: "RELAUNCH", text: "Execute with authority. Establish daily routines, habits and structure that make progress stick.", icon: Sunrise },
];

const offerings = [
  {
    tag: "Signature",
    title: "1-to-1 Coaching Walks",
    text: "A day of walking, talking and understanding in nature with Dave, tailored entirely to you.",
    bullets: ["Heightened sense of self", "Techniques to change your life", "Learn how to create freedom"],
    to: "/experiences",
    img: "/images/rx4/dave-rocks.webp",
    alt: "Dave sat by the lake with his RX4 pack",
  },
  {
    tag: "Leadership",
    title: "The Balanced Leader",
    text: "A 4-stage pathway built on 6 principles, for leaders who want growth that fits who they are.",
    bullets: ["Clarity and self-trust", "Structure that finally fits", "Long-term transformation"],
    to: "/balanced-leader",
    img: "/images/rx4/group-hike.jpg",
    alt: "Group walking a misty valley path",
  },
  {
    tag: "Immersive",
    title: "RX4 RESET Workshop",
    text: "A full-day experience of breathwork, cold water and practical tools to rewire your stress response.",
    bullets: ["Master your mindset", "Habits that stick", "Leave feeling unstoppable"],
    to: "/events",
    img: "/images/rx4/st-bees-group.jpg",
    alt: "Community on St Bees beach after a dip",
  },
];

const gallery = [
  { src: "/images/rx4/breathwork-lakeside.webp", caption: "Lakeside breathwork" },
  { src: "/images/rx4/community-dip.webp", caption: "The Sunday dip" },
  { src: "/images/rx4/dave-summit.webp", caption: "Summit at sunset" },
  { src: "/images/rx4/sauna-trailer.jpg", caption: "The Wild Sauna" },
  { src: "/images/rx4/group-hike.jpg", caption: "Valley walk" },
  { src: "/images/rx4/dave-sea-dip.jpg", caption: "St Bees, mid-dip" },
  { src: "/images/rx4/workshop-keynote.webp", caption: "Workshop in session" },
  { src: "/images/rx4/st-bees-group.jpg", caption: "After the plunge" },
  { src: "/images/rx4/sauna-inside.jpg", caption: "Inside the heat" },
];

const principles = ["Awareness", "Action", "Authenticity", "Alignment", "Accountability", "Agility"];

const benefits = [
  { icon: Mountain, title: "Build mental stability", text: "Simple, sustainable systems to support a balanced life you love." },
  { icon: Waves, title: "Connect with nature", text: "Cold water, breathwork and time outdoors with proven rewards." },
  { icon: Sunrise, title: "Establish daily routines", text: "Habits and structure that make every area of life fulfilling." },
  { icon: Compass, title: "Live a healthier life", text: "Efficient and effective ways of functioning, daily and weekly." },
];

const testimonials = [
  {
    quote: "It enabled me to switch off and absorb myself in nature much more than I usually do. Being in such a beautiful place with great people was amazing.",
    name: "Angela",
    role: "Community Dip Participant",
  },
  {
    quote: "Awesome experience. Dave and the team are professional yet down to earth. I genuinely cannot describe how invigorating it was. 12 out of 10.",
    name: "Richard",
    role: "RESET Workshop Guest",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const galleryRef = useRef(null);
  const { scrollYProgress: galleryProgress } = useScroll({ target: galleryRef, offset: ["start start", "end end"] });
  const galleryX = useTransform(galleryProgress, [0, 1], ["4%", "-72%"]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
        {/* Desktop: full-bleed portrait background */}
        <motion.div style={{ y: bgY }} className="absolute inset-0 hidden lg:block">
          <img
            src="/images/rx4/dave-hero.jpg"
            alt="David Barnes, Master Coach"
            data-testid="hero-portrait"
            className="w-full h-[120%] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink" />
        </motion.div>
        {/* Mobile: faint lakeside backdrop */}
        <div className="absolute inset-0 lg:hidden">
          <img src="/images/rx4/breathwork-lakeside.webp" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-20 pt-32 pb-16 max-w-7xl mx-auto w-full">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="overline-label mb-5"
              data-testid="hero-overline"
            >
              David Barnes / Master Coach / Lake District, UK
            </motion.p>
            <h1 className="font-display font-bold leading-[1.02] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-bone" data-testid="hero-headline">
              <MaskedLine delay={0.15}>Make Every</MaskedLine>
              <MaskedLine delay={0.3}>Day <span className="text-brand">Count.</span></MaskedLine>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="mt-8 max-w-md text-base sm:text-lg text-slate-300 leading-relaxed" data-testid="hero-subcopy">
                I help businesses and individuals flourish to become efficient and effective. Reclaim your edge in business and life.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/apply" data-testid="hero-cta-apply" className="btn-primary group">
                  Work With Me
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link to="/method" data-testid="hero-cta-method" className="btn-secondary">
                  Discover the Method
                </Link>
              </div>
              <div
                className="mt-10 pt-6 border-t border-white/15 flex flex-wrap gap-x-10 gap-y-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-400"
                data-testid="hero-trust-stats"
              >
                <span>20+ Years of Self Discovery</span>
                <span>Accredited Master Coach</span>
                <span>Wim Hof Method Instructor</span>
              </div>
            </motion.div>
          </div>

          {/* Mobile: portrait in a box with name badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-w-md mx-auto mt-14 lg:hidden"
            data-testid="hero-portrait-box"
          >
            <div className="absolute -inset-8 bg-brand/10 blur-3xl rounded-full" aria-hidden="true" />
            <img
              src="/images/rx4/dave-portrait.jpg"
              alt="David Barnes, Master Coach"
              className="relative w-full aspect-[4/5] object-cover object-top rounded-2xl border border-white/10"
            />
            <div className="absolute -bottom-5 -right-3 sm:-right-5 bg-ink border border-brand/40 rounded-2xl px-5 py-4 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.5)]">
              <p className="font-display font-semibold text-bone">David Barnes</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand mt-1">Master Coach / Wim Hof Instructor</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MEET YOUR COACH */}
      <section className="section-shell" data-testid="meet-coach-section">
        <Reveal>
          <p className="overline-label mb-6">Meet your coach</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone leading-[1.15] max-w-4xl">
            "My mission is to guide people into a better understanding of themselves. I lead by <span className="text-brand">example.</span>"
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start mt-12">
          <div className="lg:col-span-7 space-y-6">
            <Reveal delay={0.1}>
              <p className="text-base sm:text-lg text-slate-400 leading-relaxed">
                I have been on a journey of self discovery for over 20 years. In the last three I truly found the person I was looking for, and that person became RX4 Lakes. Together we will build daily habits and structure that make every area of your life a more fulfilling experience.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-3 bg-ink-soft border border-white/10 rounded-2xl px-6 py-4" data-testid="credential-master-coach">
                  <Award size={20} className="text-brand" strokeWidth={1.75} />
                  <span className="text-xs font-medium uppercase tracking-widest text-slate-300">Accredited Master Coach</span>
                </div>
                <div className="flex items-center gap-3 bg-ink-soft border border-white/10 rounded-2xl px-6 py-4" data-testid="credential-wim-hof">
                  <Wind size={20} className="text-brand" strokeWidth={1.75} />
                  <span className="text-xs font-medium uppercase tracking-widest text-slate-300">Wim Hof Method Instructor</span>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Do any of these sound like you?</p>
            <ul className="mt-4 space-y-3">
              {relates.map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-300 text-sm border-b border-white/10 pb-3" data-testid={`relate-item-${i}`}>
                  <Check size={16} className="text-brand mt-0.5 shrink-0" strokeWidth={2.5} /> {r}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-ink-soft border-y border-white/5" data-testid="story-timeline">
        <div className="section-shell">
          <Reveal className="mb-16 md:mb-24">
            <p className="overline-label mb-4">The story so far</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone">
              20 years, five chapters.
            </h2>
          </Reveal>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10 md:-translate-x-px" aria-hidden="true" />
            <div className="space-y-16 md:space-y-24">
              {timeline.map((t, i) => (
                <Reveal key={t.num} delay={0.05}>
                  <div data-testid={`timeline-entry-${t.num}`} className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-20 items-center pl-12 md:pl-0">
                    <span className="absolute left-4 md:left-1/2 top-2 w-3 h-3 rounded-full bg-brand md:-translate-x-1.5" aria-hidden="true" />
                    <div className={i % 2 === 1 ? "md:order-2" : ""}>
                      <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-2">{t.era}</p>
                      <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-bone mb-4">{t.title}</h3>
                      <p className="text-slate-400 leading-relaxed max-w-md">{t.text}</p>
                    </div>
                    <div className={i % 2 === 1 ? "md:order-1" : ""}>
                      <ParallaxImage src={t.img} alt={t.alt} className="aspect-[4/3] rounded-2xl border border-white/10" speed={0.08} />
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee items={["DREAM BIG", "START SMALL", "BE RIDICULOUSLY FAITHFUL"]} />

      {/* VALUES */}
      <section className="section-shell" data-testid="creed-section">
        <Reveal className="mb-14">
          <p className="overline-label mb-4">The creed I live by</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone">
            Principles, not platitudes.
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl">Tried and tested over two decades. Once you know what to do, changing your life is 100% accessible.</p>
        </Reveal>
        <div className="bg-ink-soft border border-white/10 rounded-2xl overflow-hidden">
          {values.map((v, i) => (
            <Reveal key={v.num} delay={i * 0.06}>
              <div
                data-testid={`value-row-${v.num}`}
                className="group flex items-baseline gap-8 md:gap-16 border-b border-white/10 last:border-b-0 px-6 md:px-10 py-7 md:py-8 transition-colors duration-300 hover:bg-ink-lift"
              >
                <span className="font-mono text-sm text-brand">{v.num}</span>
                <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-bone transition-colors duration-300 group-hover:text-brand">
                  {v.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* RX4 METHOD PILLARS */}
      <section className="bg-ink-soft border-y border-white/5" data-testid="method-section">
        <div className="section-shell">
          <Reveal className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="overline-label mb-4">The RX4 Method</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone max-w-2xl">
                Four movements. One balanced life.
              </h2>
            </div>
            <Link to="/method" data-testid="method-explore-link" className="group inline-flex items-center gap-2 text-sm font-medium text-brand hover:text-brand-hover transition-colors duration-300">
              Experience it full-screen <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {pillars.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08}>
                <div
                  data-testid={`method-pillar-${p.num}`}
                  className="group bg-ink border border-white/5 rounded-2xl p-8 md:p-10 h-full transition-all duration-300 hover:border-brand/40"
                >
                  <div className="flex items-start justify-between mb-8">
                    <span className="font-mono text-sm text-brand">[ {p.num} ]</span>
                    <p.icon size={24} className="text-slate-500 transition-colors duration-300 group-hover:text-brand" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-bone mb-3">{p.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OFFERINGS */}
      <section className="section-shell" data-testid="offerings-section">
        <Reveal className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="overline-label mb-4">Ways to work with me</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone">
              Choose your arena.
            </h2>
          </div>
          <Link to="/experiences" data-testid="offerings-view-all" className="group inline-flex items-center gap-2 text-sm font-medium text-brand hover:text-brand-hover transition-colors duration-300">
            View all experiences <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {offerings.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.1}>
              <Link
                to={o.to}
                data-testid={`offering-card-${i}`}
                className="group block bg-ink-soft border border-white/5 rounded-2xl overflow-hidden h-full transition-all duration-300 hover:border-brand/40"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={o.img} alt={o.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-black/60 backdrop-blur rounded-full text-slate-200 border border-white/10">{o.tag}</span>
                </div>
                <div className="p-7">
                  <h3 className="font-display text-xl font-semibold tracking-tight text-bone mb-2 group-hover:text-brand transition-colors duration-300">{o.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed mb-5">{o.text}</p>
                  <ul className="space-y-2">
                    {o.bullets.map((b, j) => (
                      <li key={j} className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="w-1 h-1 rounded-full bg-brand" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HORIZONTAL GALLERY */}
      <section ref={galleryRef} className="relative h-[280vh] bg-ink-soft border-y border-white/5" data-testid="gallery-section">
        <div className="sticky top-0 h-screen overflow-hidden flex flex-col justify-center">
          <div className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto w-full mb-10">
            <p className="overline-label mb-3">Field Notes</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone">
              Keep scrolling. <span className="text-brand">The story moves.</span>
            </h2>
          </div>
          <motion.div style={{ x: galleryX }} className="flex gap-6 w-max pl-6 md:pl-12 lg:pl-20">
            {gallery.map((g, i) => (
              <figure key={i} data-testid={`gallery-item-${i}`} className="relative w-[75vw] sm:w-[45vw] lg:w-[32vw] shrink-0">
                <img src={g.src} alt={g.caption} className="w-full aspect-[4/3] object-cover rounded-2xl border border-white/10" loading="lazy" />
                <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">[ {String(i + 1).padStart(2, "0")} ] {g.caption}</figcaption>
              </figure>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PRINCIPLES STRIP */}
      <section className="section-shell" data-testid="principles-strip">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <p className="overline-label mb-4">The Balanced Leader</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone">
              6 principles. Real leadership starts from within.
            </h2>
            <Link to="/balanced-leader" data-testid="principles-cta" className="btn-secondary mt-8">
              Explore the framework <ArrowUpRight size={14} />
            </Link>
          </Reveal>
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {principles.map((p, i) => (
              <Reveal key={p} delay={i * 0.06} className="bg-ink-soft">
                <div data-testid={`principle-cell-${i}`} className="p-6 md:p-8 h-full transition-colors duration-300 hover:bg-ink-lift">
                  <span className="font-mono text-xs text-brand">0{i + 1}</span>
                  <p className="font-display font-semibold text-lg text-bone mt-2">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY FINDER */}
      <JourneyFinder />

      {/* BENEFITS */}
      <section className="section-shell" data-testid="benefits-section">
        <Reveal className="mb-14">
          <p className="overline-label mb-4">Health Benefits</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone">
            What is always available to you.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08}>
              <div data-testid={`benefit-card-${i}`} className="bg-ink-soft border border-white/5 rounded-2xl p-7 h-full transition-all duration-300 hover:border-brand/40">
                <b.icon size={24} className="text-brand mb-5" strokeWidth={1.75} />
                <h3 className="font-display font-semibold text-bone mb-2">{b.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-ink-soft border-y border-white/5" data-testid="testimonials-section">
        <div className="section-shell">
          <Reveal className="mb-14">
            <p className="overline-label mb-4">What Clients Say</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone">
              Proof, not promises.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <blockquote data-testid={`testimonial-${t.name.toLowerCase()}`} className="bg-ink border border-white/10 rounded-2xl p-8 md:p-10 h-full">
                  <p className="font-display text-xl md:text-2xl font-medium leading-relaxed text-slate-200">"{t.quote}"</p>
                  <footer className="mt-8 flex items-center gap-4">
                    <span className="w-10 h-10 rounded-full bg-brand text-ink font-display font-semibold flex items-center justify-center">{t.name[0]}</span>
                    <div>
                      <p className="font-semibold text-bone text-sm">{t.name}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{t.role}</p>
                    </div>
                  </footer>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="home-final-cta">
        <ParallaxImage
          src="/images/rx4/dave-summit.webp"
          alt="Dave with arms raised on a fell at sunset"
          className="absolute inset-0 h-full"
          speed={0.18}
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative section-shell text-center flex flex-col items-center">
          <Reveal>
            <p className="overline-label mb-5">Your move</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-bone max-w-3xl leading-[1.08]">
              You will arrive feeling stuck. You will leave feeling unstoppable.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/apply" data-testid="final-cta-apply" className="btn-primary group">
                Start Your Journey
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/contact" data-testid="final-cta-contact" className="btn-secondary">
                Talk to Dave
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
